import React, { useState } from "react"
import { User } from "sharedTypes"
import { putCurrentUser, putUpdatePassword } from "api/accounts"
import * as Yup from "yup"
import _ from "lodash"
import { Box, FormLabel } from "@chakra-ui/react"
import { setCurrentUser } from "context/actions"
import { useQueryClient } from "react-query"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import ImageUpload from "components/ReactHookForm/ImageUpload"
import PasswordField from "components/ReactHookForm/PasswordField"
import TextField from "components/ReactHookForm/TextField"
import Button from "components/Buttons/Button"
import PhoneNumberField from "components/inputs/PhoneNumberField"
import validatePhoneNumber from "utilities/validatePhoneNumber"
import { PutCurrentUser, PutUpdatePassword } from "sharedTypes/api"
import { successToast } from "utilities/toasts"
import extendFormValuesWithImages from "utilities/extendFormValuesWithImages"
import { storeAuthToken } from "services/authentication"

interface EditProfileProps {
  user: User
  onClose: () => void
}

const LABEL_COL = "10rem"

// Reset component margin-bottom so the grid rowGap controls all spacing
const GRID_SX = { "& > *": { marginBottom: "0 !important" } }

const Label = ({ children }: { children: string }) => (
  <FormLabel fontSize="md" mb={0}>{children}</FormLabel>
)

const EditProfile = ({ user, onClose }: EditProfileProps) => {
  const queryClient = useQueryClient()
  const [changePassword, setChangePassword] = useState(false)

  const onSubmit = async (values) => {
    if (changePassword) {
      try {
        const { data: { authenticationToken } } = await putUpdatePassword(_.pick(values, ["password", "newPassword", "confirmPassword"]) as PutUpdatePassword)
        storeAuthToken(authenticationToken)
        successToast({ title: "Password changed" })
      } catch (e) {
        return
      }
    } else {
      const { email: oldEmailAddress } = user
      const { email: currentEmailAddress } = values
      const isEmailChanged = oldEmailAddress !== currentEmailAddress

      const { data } = await putCurrentUser(_.pick(values, ["email", "firstName", "lastName", "phone", "photo"]) as PutCurrentUser)
      if (isEmailChanged) {
        successToast({ title: `Please check ${currentEmailAddress} inbox and verify your email.` })
      }
      setCurrentUser(queryClient, data)
    }

    onClose()
  }

  const UserSchema = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    email: Yup.string().email().required(),
    phone: Yup.string()
      .test("is-valid-phone", "Phone number is not valid", (val) => (val ? validatePhoneNumber(val) : true)),
  })

  const PasswordSchema = Yup.object().shape({
    password: Yup.string().required(),
    newPassword: Yup.string().required().min(12),
    confirmPassword: Yup.string().required().oneOf([Yup.ref("newPassword")], "Passwords must match"),
  })

  if (changePassword) {
    return (
      <ModalForm
        title="Change Password"
        size="xl"
        initialValues={{ password: "", newPassword: "", confirmPassword: "" }}
        onSubmit={onSubmit}
        onClose={onClose}
        onBack={() => setChangePassword(false)}
        validationSchema={PasswordSchema}
      >
        {({ control }) => (
          <Box display="grid" gridTemplateColumns={`${LABEL_COL} 1fr`} rowGap={4} columnGap={4} alignItems="center" sx={GRID_SX}>
            <Label>Current password</Label>
            <PasswordField name="password" control={control} variant="outline" />
            <Label>New password</Label>
            <PasswordField name="newPassword" control={control} variant="outline" />
            <Label>Confirm password</Label>
            <PasswordField name="confirmPassword" control={control} variant="outline" />
          </Box>
        )}
      </ModalForm>
    )
  }

  const initialValues = extendFormValuesWithImages(_.pick(user, ["firstName", "lastName", "email", "phone"]), ["photo"])

  return (
    <ModalForm
      title="Edit Profile"
      size="xl"
      initialValues={initialValues}
      onSubmit={onSubmit}
      onClose={onClose}
      validationSchema={UserSchema}
    >
      {({ control }) => (
        <Box display="grid" gridTemplateColumns={`${LABEL_COL} 1fr`} rowGap={4} columnGap={4} alignItems="center" sx={GRID_SX}>
          <Label>Profile Photo</Label>
          <ImageUpload
            name="photo"
            control={control}
            imageUrl={user.photoUrl.includes("data.diobox.com") ? "" : user.photoUrl}
            imageWidth="100px"
            imageHeight="100px"
            imageFit="cover"
          />
          <Label>First Name</Label>
          <TextField name="firstName" control={control} variant="outline" />
          <Label>Last Name</Label>
          <TextField name="lastName" control={control} variant="outline" />
          <Label>Email</Label>
          <TextField name="email" control={control} type="email" autoComplete="email" variant="outline" />
          <Label>Phone</Label>
          <PhoneNumberField name="phone" control={control} defaultCountry="US" />
          <Label>Password</Label>
          <Box>
            <Button size="sm" variant="outline" onClick={() => setChangePassword(true)}>Change Password</Button>
          </Box>
        </Box>
      )}
    </ModalForm>
  )
}

export default EditProfile
